import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { assetCatalog } from "@/api/client";
import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { MultiSelect } from "@/components/MultiSelect";
import { PageHead } from "@/components/PageHead";
import { Select } from "@/components/inputs";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { useKindParam } from "@/lib/hooks/useKindParam";
import { useCompanies } from "@/modules/registry/parts_common";
import { useProductCompanies } from "@/modules/products/data";
import { KindSelect, ModelChip, QK, kindLabel, useModelPool, useTemplates } from "@/modules/asset_catalog/common";
import { ScopeBar, ScopeField } from "@/modules/products/ScopeBar";

/** Who may build with a model — a page of its own (Dattatray, 2026-09-28).
 *
 *  This control has now been moved twice, and the second move is the one that
 *  sticks. It was a section of the Products spec editor — *"we should manage
 *  it in Asset Catalog model and not in products"* — and then a section of the
 *  model FORM, which he also refused: *"I think this needs a separate sub page
 *  in the asset catalog instead of being a selection in the Product models."*
 *
 *  He is right, and the reason is that it is not part of a model's
 *  SPECIFICATION. Everything else on that form describes what the thing IS —
 *  voltage, chemistry, what is fitted inside it. Visibility describes who may
 *  USE it, which changes on a commercial decision, long after the design is
 *  frozen, and is edited by somebody who is not editing the design. Putting a
 *  commercial switch inside an engineering form means opening the engineering
 *  form to change it, on a version that may be retired and read-only.
 *
 *  ONE EDITING HOME. The Overview keeps its "Visible to" COLUMN, because that
 *  is reading; its button now brings you here with the version already chosen
 *  rather than opening a modal that edits the same field somewhere else.
 *
 *  EMPTY MEANS EVERY COMPANY, not none. That is the opposite of
 *  `firmware_compatibility`, where no rows means the build reaches nothing —
 *  two conventions one join apart, each stated where it lives, because the
 *  blast radius differs: an over-wide catalogue entry is clutter, an
 *  over-wide firmware target is a bricked board.
 */
export function AssetCatalogVisibilityPage() {
  const [sp, setSp] = useSearchParams();
  const [kind, setKind] = useKindParam();
  const [versionId, setVersionId] = useState(() => sp.get("id") ?? "");

  // WRITE IS GATED, READ IS NOT — and this was missing until
  // `tests/ui/test_catalog_visibility_control.py` failed on it. The modal this
  // page replaced was rendered behind `canManage`; lifting the control onto a
  // page of its own quietly dropped that, so anyone who could open the module
  // could change who may build with a model. The old tests caught it on the
  // first run, which is the whole argument for retargeting them instead of
  // deleting them with the design they described.
  //
  // Disabled rather than hidden: a reader still needs to SEE the current
  // scope, and a hidden control reads as "not set".
  const mayManage = useSession().can(CAP.MANAGE_CATALOG);
  const tpl = useTemplates().data;
  const pool = useModelPool().data?.models ?? [];
  const allCompanies = useCompanies().data?.companies ?? [];
  // WHO HOLDS THE PRODUCT. Dattatray: *"the model visibility dropdown shall
  // have only those companies to whom that product is visible, right? because
  // right now we are giving full list of companies."*
  //
  // He is right, and it is not cosmetic. Reach is a NARROWING of entitlement:
  // granting a model to a company that does not hold the product produces a
  // row that can never mean anything, because the product gate refuses them
  // first. Offering the full list invites exactly that.
  const holders = useProductCompanies(kind).data?.companies
    ?.filter((c) => c.holds_product).map((c) => c.company_id) ?? [];
  const qc = useQueryClient();
  const fb = useMutationFeedback();

  const model = pool.find((m) => m.version_id === versionId);

  // Arriving with `?id=` from the Overview: adopt that model's kind so the
  // product picker shows where you are rather than reading as unset.
  useEffect(() => {
    if (model && !kind) setKind(model.kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.version_id]);

  const [ids, setIds] = useState<string[]>([]);
  // Reload the selection whenever the model changes — including to nothing,
  // so a cleared picker cannot leave the previous model's companies on screen
  // ready to be saved onto the next one (D-84, one level down).
  useEffect(() => { setIds(model?.companies ?? []); }, [model?.version_id, model?.companies]);

  const choose = (id: string) => {
    setVersionId(id);
    setSp((prev) => {
      const out = new URLSearchParams(prev);
      if (id) out.set("id", id); else out.delete("id");
      return out;
    }, { replace: true });
  };

  const save = useMutation({
    mutationFn: () => assetCatalog.setModelCompanies(versionId, ids),
    onSuccess: () => { fb.ok(); void qc.invalidateQueries({ queryKey: QK }); },
    onError: fb.fail,
  });

  const models = pool
    .filter((m) => (kind ? m.kind === kind : true) && m.status !== "retired")
    .sort((a, b) => a.name.localeCompare(b.name) || a.version - b.version);

  // The OFFER is the holders — plus anyone already granted, so an existing
  // grant never vanishes from the control just because entitlement changed
  // later. A company silently dropping out of the picker while its row stays
  // in the database is the same invisible-state problem one level down.
  //
  // Falls back to every company when the holder list has not loaded (or the
  // reader lacks the products gate), because an EMPTY picker reads as "no
  // companies exist" rather than "still loading".
  const offered = holders.length || ids.length
    ? allCompanies.filter((c) => holders.includes(c.company_id) || ids.includes(c.company_id))
    : allCompanies;

  const dirty = model
    ? [...ids].sort().join() !== [...(model.companies ?? [])].sort().join()
    : false;

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Asset Catalog"
        title="Model visibility"
        desc="Which companies may build with a model. Empty means every company — and anyone who already owns something built on it keeps seeing it on their own equipment either way."
      />

      <ScopeBar>
        <ScopeField label="Product">
          {/* `KindSelect`, not a hand-rolled <Select>. Dattatray: *"Product
              selector lists the products plainly and not as per type of
              product."* The shared component groups by family (`<optgroup>`
              per Power / Drive / Control & display) and carries the abbr —
              which is how every other product picker in the console reads.
              Thirteen flat options is a list you read; five labelled groups
              is one you scan. CLAUDE.md says to look in the module for a
              shared control before writing one; this is the second time in a
              day I did not. */}
          <KindSelect kinds={tpl?.kinds ?? []} value={kind}
            placeholder="Select a product…"
            onChange={(v) => { setKind(v); choose(""); }}
            style={{ minWidth: 240 }} />
        </ScopeField>

        <ScopeField label="Model">
          <Select value={versionId} onChange={(e) => choose(e.target.value)}
            disabled={!models.length} style={{ minWidth: 260 }}>
            <option value="">
              {models.length ? "Select a model…" : "No models for this product"}
            </option>
            {models.map((m) => (
              <option key={m.version_id} value={m.version_id}>
                {m.name} v{m.version}
              </option>
            ))}
          </Select>
        </ScopeField>
      </ScopeBar>

      {!model ? (
        <div className="panel panel-pad cap">
          Choose a product and a model to set who may build with it.
        </div>
      ) : (
        <div className="panel panel-pad">
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <span style={{ fontWeight: 700 }}>{model.name}</span>
            <span className="mono cap">v{model.version}</span>
            <ModelChip st={model.status} />
            <span className="cap">{kindLabel(tpl, model.kind)}</span>
            {ids.length === 0
              ? <Chip tone="ok">every company</Chip>
              : <Chip tone="warn">{ids.length} compan{ids.length === 1 ? "y" : "ies"}</Chip>}
          </div>

          <div className="cap" style={{ marginBottom: 8, maxWidth: "72ch" }}>
            Companies that may register assets and subcomponents against this
            model. Empty = <b>every</b> company. Anyone who already owns
            something built on it keeps seeing it on their own equipment either
            way — restricting hides it from pickers, it never blanks out
            history.
          </div>

          <div style={{ maxWidth: 460 }}>
            <MultiSelect
              options={offered.map((c) => ({ value: c.company_id, label: c.name }))}
              value={ids} onChange={setIds} disabled={!mayManage}
              emptyLabel="All companies" clearLabel="Clear — all companies" />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
            {mayManage && <button className="btn teal sm" disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save"}
            </button>}
            {mayManage && dirty && (
              <button className="btn ghost sm"
                onClick={() => setIds(model.companies ?? [])}>Discard</button>
            )}
            {fb.errorNode}
          </div>
        </div>
      )}
    </div>
  );
}
